import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { CheckCircle, Circle, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { organizationService } from '@/services/api/organization.service';

interface SetupProgressProps {
  className?: string;
}

// Backend step structure (actual API response)
interface OnboardingStep {
  step: number;
  name: string;
  description: string;
  completed: boolean;
  required: boolean;
}

// Map step names to action paths
const STEP_ACTIONS: Record<string, string> = {
  'Services Setup': '/admin/services',
  'Team Setup': '/admin/team',
  'Availability': '/admin/availability',
  'Business Information': '/admin/settings',
};

export function SetupProgress({ className }: SetupProgressProps) {
  const { t } = useTranslation('tips');

  const { data, isLoading } = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: () => organizationService.getOnboardingStatus(),
  });

  if (isLoading || !data) {
    return (
      <div className={cn('bg-white rounded-lg border border-slate-200 p-5', className)}>
        <div className="animate-pulse">
          <div className="h-4 bg-slate-200 rounded w-32 mb-4" />
          <div className="h-2 bg-slate-200 rounded w-full mb-4" />
          <div className="space-y-3">
            <div className="h-4 bg-slate-200 rounded w-48" />
            <div className="h-4 bg-slate-200 rounded w-40" />
            <div className="h-4 bg-slate-200 rounded w-44" />
          </div>
        </div>
      </div>
    );
  }

  const { onboarding } = data;
  const percentage = onboarding.completion_percentage;

  // Cast steps to the actual backend structure and filter out "Account Creation"
  const steps = ((onboarding.steps || []) as unknown as OnboardingStep[]).filter(
    (step) => step.name !== 'Account Creation'
  );

  const completedCount = steps.filter((s) => s.completed).length;
  const totalCount = steps.length;

  return (
    <div className={cn('bg-white rounded-lg border border-slate-200 p-5', className)}>
      {/* Header with percentage */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-900">
          {t('progress.title', 'Setup Progress')}
        </h3>
        <span className="text-sm font-medium text-slate-600">
          {percentage}%
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-slate-100 rounded-full mb-4 overflow-hidden">
        <div
          className="h-full bg-green-500 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* Completion count */}
      <p className="text-xs text-slate-500 mb-4">
        {completedCount} {t('progress.of', 'of')} {totalCount} {t('progress.complete', 'complete')}
      </p>

      {/* Steps list */}
      <div className="space-y-3">
        {steps.map((step) => {
          const actionPath = STEP_ACTIONS[step.name];

          return (
            <div
              key={step.step}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                {step.completed ? (
                  <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                ) : (
                  <Circle className="h-4 w-4 text-slate-300 shrink-0" />
                )}
                <span
                  className={cn(
                    'text-sm',
                    step.completed ? 'text-slate-700' : 'text-slate-500'
                  )}
                >
                  {t(`progress.steps.${step.name}`, step.name)}
                </span>
              </div>

              {/* Show action button for incomplete steps */}
              {!step.completed && actionPath && (
                <Link href={actionPath}>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1">
                    {t('progress.actions.setup', 'Set up')}
                    <ArrowRight className="h-3 w-3" />
                  </Button>
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
